import { createSelector } from 'reselect';
import { RootState } from '../../../types';
import { IEntityReducer } from 'core/reducers/entities';
import { EntityTypes } from 'constants/entities';
import { IOrganisationUser, IOrganisationUserReport } from 'types/domain/user';
import { parseUserReport } from './users';
import { ISearch, SortBy } from 'types/domain/search';
import { convertDate } from 'utils/convertDate';
import { IReport, ReportType } from 'core/slices/report';

const entity = (state: RootState) => state.entity.present;
const search = (state: RootState) => state.core.search;
const report = (state: RootState) => state.core.report.reports[ReportType.LATEST];

export const selectLatestUsersReport = createSelector(
  [entity, search, report],
  (entity: IEntityReducer, search: ISearch, report: IReport) => {
    return Object.values(entity[EntityTypes.ORG_USER])
      .filter(g => g.status > -1)
      .filter(g => g.signin != null)
      .filter(g => (search.term ? searchUserName(g, search.term) : true))
      .filter(g => (search.sort === SortBy.NAME ? getUserName(g) != null : true))
      .map((user: IOrganisationUser) => parseUserReport(entity, user.id))
      .sort((a, b) => sortByNameModified(a, b, search, report));
  },
);

export function sortByNameModified(
  a,
  b,
  search: ISearch,
  report: IReport,
) {
  if (a === null) return -1;
  if (b === null) return 1;

  switch (search.sort) {
    case SortBy.NAME:
      if (getUserName(a) == null) return 1;
      if (getUserName(b) == null) return -1;

      return getUserName(a).localeCompare(getUserName(b));
    case SortBy.TOTAL:
      if (a.organisationUserVariables == null) return 1;
      if (b.organisationUserVariables == null) return -1;
      return Object.keys(a.organisationUserVariables).length > Object.keys(b.organisationUserVariables).length ? -1 : 1;
    default:

      if (convertDate(a.signin?.lastLoginSignin?.modified?.date || a.signin?.lastLoginSignin?.created?.date) == null)
        return  1;
      if (convertDate(b.signin?.lastLoginSignin?.modified?.date || b.signin?.lastLoginSignin?.created?.date) == null) 
         return -1;

      return convertDate(a.signin?.lastLoginSignin?.modified?.date || a.signin?.lastLoginSignin?.created?.date) >
        convertDate(b.signin?.lastLoginSignin?.modified?.date || b.signin?.lastLoginSignin?.created?.date)
        ? -1
        : 1;
  }
}

export function getUserName(user: IOrganisationUser) {
  return user.firstName || user.lastName || user.email;
}

export function searchUserName(user: IOrganisationUser, term) {
  return user && term
    ? user.firstName?.toLowerCase().includes(term.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(term.toLowerCase()) ||
        user.email?.toLowerCase().includes(term.toLowerCase())
    : null;
}
